/** বিশেষ তথ্য ফরম (ফরম-২ এর অতিরিক্ত তথ্য) — data helpers. */

export const INITIAL_SPECIAL_INFORMATION_FORM = {
    // masthead
    formNo: '',
    // ১-২
    nameBn: '',
    fatherName: '',
    // ৩ — father's / husband's permanent address
    fDivision: '',
    fDistrict: '',
    fUpazila: '',
    fLocalBody: '',
    fWardNo: '',
    fMouza: '',
    fVillage: '',
    fHolding: '',
    fPostOffice: '',
    fPostCode: '',
    // ৪ — legal guardian, when the parents are no longer living
    guardianName: '',
    guardianNid: '',
    guardianRelation: '',
    guardianBirthDate: '',
    // ৫-৬ — paternal grandfather
    grandfatherName: '',
    grandfatherNid: '',
    grandfatherNidNa: false,
    // ৭ — grandfather's permanent address
    gDivision: '',
    gDistrict: '',
    gUpazila: '',
    gLocalBody: '',
    gWardNo: '',
    gMouza: '',
    gVillage: '',
    gHolding: '',
    gPostOffice: '',
    gPostCode: '',
    // ৮ — three NID-holding blood relatives
    rel1Name: '',
    rel1Nid: '',
    rel1Relation: '',
    rel1BirthDate: '',
    rel1Mobile: '',
    rel2Name: '',
    rel2Nid: '',
    rel2Relation: '',
    rel2BirthDate: '',
    rel2Mobile: '',
    rel3Name: '',
    rel3Nid: '',
    rel3Relation: '',
    rel3BirthDate: '',
    rel3Mobile: '',
    // ৯ — how the current address is held
    residenceType: '',
    rentFrom: '',
    rentTo: '',
    rentOwnerName: '',
    rentOwnerNid: '',
    otherOwnerName: '',
    otherOwnerNid: '',
    otherFrom: '',
    otherTo: '',
    // ১০ — applicants born abroad
    arrivalDate: '',
    passportIssueDate: '',
    passportIssuePlace: '',
    passportExpiryDate: '',
    // ১১ — present occupation
    jobJoinDate: '',
    employerName: '',
    employerAddress: '',
    employerAddress2: '',
    employerMobile: '',
    // ১২ — last two educational institutions
    edu1Name: '',
    edu1From: '',
    edu1To: '',
    edu1Result: '',
    edu2Name: '',
    edu2From: '',
    edu2To: '',
    edu2Result: '',
    // ১৪ — the enumerator who collected the information
    collectorNid: '',
};

/** The three tenures item ৯ offers, in the order the form prints them. */
export const RESIDENCE_TYPES = ['own', 'rent', 'other'];

/** Item ৯'s two conditional branches — only one of them is ever the applicant's. */
const RENT_ONLY_FIELDS = ['rentFrom', 'rentTo', 'rentOwnerName', 'rentOwnerNid'];
const OTHER_ONLY_FIELDS = ['otherOwnerName', 'otherOwnerNid', 'otherFrom', 'otherTo'];

/**
 * Whether this applicant can fill a field at all, given what they have answered.
 *
 * Item ৯ takes the tenure first and then only the details of the tenure chosen;
 * the editor hides the other branch, so those blanks are not theirs to fill.
 * Item ৬'s NID box goes the same way once «প্রযোজ্য নহে» is ticked. A blank that
 * does not apply is not missing, must not be validated, and must not print —
 * the answer is kept in the form, so changing the tenure back brings it up
 * again, but nothing that contradicts the ticked box reaches the sheet.
 *
 * @param {typeof INITIAL_SPECIAL_INFORMATION_FORM} form
 * @param {string} key
 */
export function isSpecialInformationFieldApplicable(form, key) {
    if (RENT_ONLY_FIELDS.includes(key)) return form?.residenceType === 'rent';
    if (OTHER_ONLY_FIELDS.includes(key)) return form?.residenceType === 'other';
    if (key === 'grandfatherNid') return !form?.grandfatherNidNa;
    return true;
}

export function displayField(value) {
    const v = String(value ?? '').trim();
    return v || '';
}

/** Convert Bengali/Arabic-Indic digits to ASCII — used only for validation logic. */
function toAsciiDigits(str) {
    return str
        .replace(/[০-৯]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x09e6 + 48))
        .replace(/[٠-٩]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48));
}

const BENGALI_DIGITS = '০১২৩৪৫৬৭৮৯';

/** The form is printed in Bengali, so its numbers are written in Bengali too. */
export function toBengaliDigits(value) {
    return String(value ?? '').replace(/\d/g, (digit) => BENGALI_DIGITS[Number(digit)]);
}

/**
 * A date as the form writes it: দিন-মাস-বছর in Bengali numerals. The comb rows
 * take the same value with its separators dropped, one digit per box.
 * @param {Date | null} date
 */
export function formatDateField(date) {
    if (!date || Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return toBengaliDigits(`${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`);
}

/**
 * Read a stored date back for the picker. Bengali or ASCII digits are both
 * accepted, and -, / or . as the separator.
 * @returns {Date | null}
 */
export function parseDateField(value) {
    const match = toAsciiDigits(String(value ?? '').trim())
        .match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    if (!match) return null;
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const date = new Date(year, month - 1, day);
    // Reject a date the calendar rolled over, e.g. 31-02-2020.
    if (date.getDate() !== day || date.getMonth() !== month - 1) return null;
    return date;
}

/** Keep only digit characters (ASCII 0-9, Bengali ০-৯, Arabic-Indic ٠-٩). */
export function normalizeDigits(value, maxLength) {
    const digits = String(value ?? '').replace(/[^\d০-৯٠-٩]/g, '');
    return maxLength ? digits.slice(0, maxLength) : digits;
}

/** Strip to digits, drop a leading 880 country code if present. */
export function normalizeBdPhone(value) {
    let digits = String(value ?? '').replace(/[^\d০-৯٠-٩]/g, '');
    const ascii = toAsciiDigits(digits);
    if (ascii.startsWith('880') && ascii.length >= 13) {
        digits = digits.slice(3);
    }
    return digits.slice(0, 11);
}

/** Every Bangladeshi telephone or mobile number: 11 digits starting with 0. */
export function isValidBdPhone(value) {
    const ascii = toAsciiDigits(normalizeBdPhone(value));
    return /^0\d{10}$/.test(ascii);
}

export function getMobileValidationError(value, t) {
    const digits = normalizeBdPhone(value);
    if (!digits) return '';
    if (digits.length < 11) {
        return t('specialInformationForm.validation.mobileLength', { count: digits.length });
    }
    if (!isValidBdPhone(digits)) {
        return t('specialInformationForm.validation.mobileInvalid');
    }
    return '';
}

/**
 * A national ID is 10, 13 or 17 digits. Anything else is a typo worth flagging,
 * but only once the field is long enough that the applicant has stopped typing.
 */
export function getNidValidationError(value, t) {
    const digits = normalizeDigits(value, 17);
    if (!digits) return '';
    if (![10, 13, 17].includes(digits.length)) {
        return t('specialInformationForm.validation.nidLength');
    }
    return '';
}

/** Turn a stored value into the digits a comb row prints, one per cell. */
export function combDigits(value, cellCount) {
    const digits = String(value ?? '').replace(/[^\d০-৯٠-٩]/g, '');
    return toBengaliDigits(digits).slice(0, cellCount);
}

const FORM_SNAPSHOT_KEY = 'special-information-form-last-snapshot';

/**
 * What the committee will not accept the sheet without. The rest of the form is
 * conditional — the guardian block only applies when the parents are no longer
 * living, items ৯-১০ depend on how the applicant lives and where they were
 * born — so none of it is required here.
 */
const REQUIRED_FIELDS = [
    'nameBn',
    'fatherName',
    'fDivision',
    'fDistrict',
    'fUpazila',
    'grandfatherName',
    'rel1Name',
    'rel1Nid',
    'rel1Relation',
];

/** @returns {typeof INITIAL_SPECIAL_INFORMATION_FORM | null} */
export function getSpecialInformationFormSnapshot() {
    if (typeof window === 'undefined') return null;
    try {
        const raw = localStorage.getItem(FORM_SNAPSHOT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return { ...INITIAL_SPECIAL_INFORMATION_FORM, ...parsed };
    } catch {
        return null;
    }
}

/** Save the latest sheet so it can be restored later. */
export function saveSpecialInformationFormSnapshot(form) {
    if (typeof window === 'undefined' || !form) return;
    try {
        const payload = {};
        for (const key of Object.keys(INITIAL_SPECIAL_INFORMATION_FORM)) {
            const initial = INITIAL_SPECIAL_INFORMATION_FORM[key];
            payload[key] = typeof initial === 'boolean' ? Boolean(form[key]) : displayField(form[key]);
        }
        localStorage.setItem(FORM_SNAPSHOT_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

/**
 * @param {typeof INITIAL_SPECIAL_INFORMATION_FORM} form
 * @param {(key: string, opts?: object) => string} t
 * @returns {{ field: string, message: string }[]}
 */
export function getSpecialInformationFormValidationErrors(form, t) {
    const errors = [];

    for (const key of REQUIRED_FIELDS) {
        if (!displayField(form?.[key])) {
            errors.push({
                field: key,
                message: t('specialInformationForm.validation.required', {
                    field: t(`specialInformationForm.fields.${key}`),
                }),
            });
        }
    }

    for (const key of ['rel1Mobile', 'rel2Mobile', 'rel3Mobile', 'employerMobile']) {
        const err = getMobileValidationError(form?.[key], t);
        if (err) errors.push({ field: key, message: err });
    }

    for (const key of ['guardianNid', 'grandfatherNid', 'rel1Nid', 'rel2Nid', 'rel3Nid', 'rentOwnerNid', 'otherOwnerNid', 'collectorNid']) {
        // A number left behind by a tenure the applicant has since changed is
        // not theirs to correct — it is hidden, and it no longer prints.
        if (!isSpecialInformationFieldApplicable(form, key)) continue;
        const err = getNidValidationError(form?.[key], t);
        if (err) errors.push({ field: key, message: err });
    }

    return errors;
}

export function isSpecialInformationFormComplete(form, t) {
    return getSpecialInformationFormValidationErrors(form, t).length === 0;
}

/**
 * Every blank still empty, named the way the form asks for it and in the order
 * it asks — what the download confirmation lists back to the applicant.
 *
 * `grandfatherNidNa` is a tick, not a blank, so it never counts as missing, and
 * a blank the applicant cannot reach — the other tenure's details, or item ৬'s
 * NID once that tick is set — is not missing either.
 *
 * @param {typeof INITIAL_SPECIAL_INFORMATION_FORM} form
 * @param {(key: string, opts?: object) => string} t
 * @returns {string[]}
 */
export function getEmptySpecialInformationFields(form, t) {
    return Object.keys(INITIAL_SPECIAL_INFORMATION_FORM)
        .filter((key) => typeof INITIAL_SPECIAL_INFORMATION_FORM[key] !== 'boolean')
        .filter((key) => isSpecialInformationFieldApplicable(form, key))
        .filter((key) => !displayField(form?.[key]))
        .map((key) => t(`specialInformationForm.fields.${key}`));
}

/** Everything the preview prints, already trimmed. */
export function buildSpecialInformationSheet(form) {
    const out = {};
    for (const key of Object.keys(INITIAL_SPECIAL_INFORMATION_FORM)) {
        const initial = INITIAL_SPECIAL_INFORMATION_FORM[key];
        // An answer that no longer applies stays in the form but leaves the sheet.
        if (!isSpecialInformationFieldApplicable(form, key)) {
            out[key] = typeof initial === 'boolean' ? false : '';
            continue;
        }
        out[key] = typeof initial === 'boolean' ? Boolean(form?.[key]) : displayField(form?.[key]);
    }
    return out;
}

/** @param {{ field: string, message: string }[]} errors */
export function validationErrorsToFieldMap(errors) {
    const map = {};
    for (const { field, message } of errors) {
        map[field] = message;
    }
    return map;
}
